import { useState, useEffect, useRef, useCallback } from 'react';
import { Select, Spin, Tabs, Tag } from 'antd';
import { LuBadgeCheck } from 'react-icons/lu';
import FeedList from './FeedList.jsx';
import './style.scss';
import { useSelector } from 'react-redux';
import { downloadFromS3Signed } from 'src/_shared/services/utils';

const Feed = (props) => {
	const {
		handleReplySubmit,
		handleSetToPrivate,
		handleClapClick,
		newestFeedData,
		topFeedData,
		teamFeedData,
		milestonesFeedData,
		newestFeedLoading,
		topFeedLoading,
		teamFeedLoading,
		milestonesFeedLoading,
		hasMoreNewestFeedData,
		hasMoreTopFeedData,
		hasMoreTeamFeedData,
		hasMoreMilestonesFeedData,
		isLoadingMoreNewestFeed,
		isLoadingMoreTopFeed,
		isLoadingMoreTeamFeed,
		isLoadingMoreMilestonesFeed,
		loadMoreNewestFeed,
		loadMoreTopFeed,
		loadMoreTeamFeed,
		loadMoreMilestonesFeed,
		tagsFeed,
		selectedTags = [],
		setSelectedTags,
	} = props;
	const [source, setSource] = useState(null);
	const [activeTab, setActiveTab] = useState('1');
	const currentUser = useSelector((state) => state?.user?.currentUser);
	const availableTags = (
		currentUser?.company?.recognitionSettings?.recognitionHashtags || []
	)
		.slice()
		.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
	const tagOptions = availableTags.map((tag) => {
		return { label: `#${tag}`, value: tag };
	});
	const enableMilestonesTab = currentUser?.featureFlags?.includes(
		'enableMilestonesTab'
	);
	const newestSentinelRef = useRef(null);
	const topSentinelRef = useRef(null);
	const teamSentinelRef = useRef(null);
	const milestonesSentinelRef = useRef(null);
	const tagsSentinelRef = useRef(null);
	const [topSentinelReady, setTopSentinelReady] = useState(false);
	const [teamSentinelReady, setTeamSentinelReady] = useState(false);
	const [milestonesSentinelReady, setMilestonesSentinelReady] = useState(false);
	const [tagsSentinelReady, setTagsSentinelReady] = useState(false);

	const tagsFeedData = tagsFeed?.tagsFeedData;
	const tagsFeedLoading = tagsFeed?.tagsFeedLoading;
	const hasMoreTagsFeedData = tagsFeed?.hasMoreTagsFeedData;
	const isLoadingMoreTagsFeed = tagsFeed?.isLoadingMoreTagsFeed;
	const loadMoreTagsFeed = tagsFeed?.loadMoreTagsFeed;

	const handleSelectTags = (value = []) => {
		setSelectedTags(value);
		if (value.length === 0) {
			setActiveTab('1');
		}
	};

	const filterTagOption = (input, option) => {
		// Remove leading '#' from input if present to allow matching
		const searchValue = input.startsWith('#') ? input.slice(1) : input;
		return (option?.value ?? '')
			.toLowerCase()
			.includes(searchValue.toLowerCase());
	};

	useEffect(() => {
		if (selectedTags.length > 0) {
			setActiveTab('5');
		}
	}, [selectedTags]);

	const setTopSentinelRef = useCallback((node) => {
		if (node) {
			topSentinelRef.current = node;
			setTopSentinelReady(true);
		}
	}, []);

	const setTeamSentinelRef = useCallback((node) => {
		if (node) {
			teamSentinelRef.current = node;
			setTeamSentinelReady(true);
		}
	}, []);

	const setMilestonesSentinelRef = useCallback((node) => {
		if (node) {
			milestonesSentinelRef.current = node;
			setMilestonesSentinelReady(true);
		}
	}, []);

	const setTagsSentinelRef = useCallback((node) => {
		if (node) {
			tagsSentinelRef.current = node;
			setTagsSentinelReady(true);
		}
	}, []);

	useEffect(() => {
		const fetchAvatar = async () => {
			if (currentUser.avatar) {
				const presignedUrl = await downloadFromS3Signed(currentUser.avatar.key);
				setSource(presignedUrl);
			}
		};

		fetchAvatar();
	}, [currentUser?.avatar?.key]);

	const teamLabel =
		'My ' + (currentUser?.company?.subCompanyLabel || 'SubCompany');

	const renderNoMoreData = () => (
		<>
			<div
				className="comment-spacer"
				style={{
					height: '0px',
					borderBottom: '1px solid #e0e0e0',
					margin: '16px 0',
					width: '100%',
				}}
			/>
			<div
				style={{
					display: 'flex',
					gap: '4px',
					padding: '20px',
					color: '#595959',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<LuBadgeCheck />
				<span>You've reached the end of the feed.</span>
			</div>
		</>
	);

	useEffect(() => {
		const tabConfigs = {
			1: {
				ref: newestSentinelRef,
				hasMore: hasMoreNewestFeedData,
				isLoading: isLoadingMoreNewestFeed,
				loadMore: loadMoreNewestFeed,
				isReady: true,
			},
			2: {
				ref: topSentinelRef,
				hasMore: hasMoreTopFeedData,
				isLoading: isLoadingMoreTopFeed,
				loadMore: loadMoreTopFeed,
				isReady: topSentinelReady,
			},
			3: {
				ref: teamSentinelRef,
				hasMore: hasMoreTeamFeedData,
				isLoading: isLoadingMoreTeamFeed,
				loadMore: loadMoreTeamFeed,
				isReady: teamSentinelReady,
			},
			4: {
				ref: milestonesSentinelRef,
				hasMore: hasMoreMilestonesFeedData,
				isLoading: isLoadingMoreMilestonesFeed,
				loadMore: loadMoreMilestonesFeed,
				isReady: milestonesSentinelReady,
			},
			5: {
				ref: tagsSentinelRef,
				hasMore: hasMoreTagsFeedData,
				isLoading: isLoadingMoreTagsFeed,
				loadMore: loadMoreTagsFeed,
				isReady: tagsSentinelReady,
			},
		};

		const config = tabConfigs[activeTab];

		if (
			!config ||
			!config.hasMore ||
			config.isLoading ||
			!config.loadMore ||
			!config.isReady ||
			!config.ref.current
		) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && config.hasMore && !config.isLoading) {
						config.loadMore();
					}
				});
			},
			{
				root: null,
				rootMargin: '100px',
				threshold: 0.1,
			}
		);

		observer.observe(config.ref.current);

		return () => {
			observer.disconnect();
		};
	}, [
		activeTab,
		hasMoreNewestFeedData,
		hasMoreTopFeedData,
		hasMoreTeamFeedData,
		hasMoreMilestonesFeedData,
		hasMoreTagsFeedData,
		isLoadingMoreNewestFeed,
		isLoadingMoreTopFeed,
		isLoadingMoreTeamFeed,
		isLoadingMoreMilestonesFeed,
		isLoadingMoreTagsFeed,
		loadMoreNewestFeed,
		loadMoreTopFeed,
		loadMoreTeamFeed,
		loadMoreMilestonesFeed,
		loadMoreTagsFeed,
		topSentinelReady,
		teamSentinelReady,
		milestonesSentinelReady,
		tagsSentinelReady,
	]);

	const tabItems = [
		{
			key: '1',
			label: 'Newest',
			children: newestFeedLoading ? (
				<div
					style={{
						minHeight: 800,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Spin />
				</div>
			) : (
				<div className="scroll-card">
					<FeedList
						currentUser={currentUser}
						userAvatarSrc={source}
						feedListData={newestFeedData}
						handleClapCount={handleClapClick}
						isFeedLoading={newestFeedLoading}
						handleReplySubmit={handleReplySubmit}
						handleSetToPrivate={handleSetToPrivate}
						handleSelectTags={handleSelectTags}
						availableTags={availableTags}
						isLoadingMore={isLoadingMoreNewestFeed}
					/>
					<div
						ref={newestSentinelRef}
						style={{ height: '1px', width: '100%' }}
					/>
					{isLoadingMoreNewestFeed && (
						<div style={{ textAlign: 'center', padding: '20px' }}>
							<Spin />
						</div>
					)}
					{!hasMoreNewestFeedData &&
						newestFeedData?.length > 0 &&
						renderNoMoreData()}
				</div>
			),
		},
		{
			key: '2',
			label: 'Top',
			children: topFeedLoading ? (
				<div
					style={{
						minHeight: 800,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Spin />
				</div>
			) : (
				<div className="scroll-card">
					<FeedList
						currentUser={currentUser}
						userAvatarSrc={source}
						feedListData={topFeedData}
						handleClapCount={handleClapClick}
						isFeedLoading={topFeedLoading}
						handleReplySubmit={handleReplySubmit}
						handleSetToPrivate={handleSetToPrivate}
						handleSelectTags={handleSelectTags}
						availableTags={availableTags}
						isLoadingMore={isLoadingMoreTopFeed}
					/>
					<div
						ref={setTopSentinelRef}
						style={{ height: '1px', width: '100%' }}
					/>
					{isLoadingMoreTopFeed && (
						<div style={{ textAlign: 'center', padding: '20px' }}>
							<Spin />
						</div>
					)}
					{!hasMoreTopFeedData && topFeedData?.length > 0 && renderNoMoreData()}
				</div>
			),
		},
		{
			key: '3',
			label: teamLabel,
			children: teamFeedLoading ? (
				<div
					style={{
						minHeight: 800,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Spin />
				</div>
			) : (
				<div className="scroll-card">
					<FeedList
						currentUser={currentUser}
						userAvatarSrc={source}
						feedListData={teamFeedData}
						handleClapCount={handleClapClick}
						isFeedLoading={teamFeedLoading}
						handleReplySubmit={handleReplySubmit}
						handleSetToPrivate={handleSetToPrivate}
						handleSelectTags={handleSelectTags}
						availableTags={availableTags}
						isLoadingMore={isLoadingMoreTeamFeed}
					/>
					<div
						ref={setTeamSentinelRef}
						style={{ height: '1px', width: '100%' }}
					/>
					{isLoadingMoreTeamFeed && (
						<div style={{ textAlign: 'center', padding: '20px' }}>
							<Spin />
						</div>
					)}
					{!hasMoreTeamFeedData &&
						teamFeedData?.length > 0 &&
						renderNoMoreData()}
				</div>
			),
		},
		enableMilestonesTab
			? {
					key: '4',
					label: 'Milestones',
					children: milestonesFeedLoading ? (
						<div
							style={{
								minHeight: 800,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Spin />
						</div>
					) : (
						<div className="scroll-card">
							<FeedList
								currentUser={currentUser}
								userAvatarSrc={source}
								feedListData={milestonesFeedData}
								handleClapCount={handleClapClick}
								isFeedLoading={milestonesFeedLoading}
								handleReplySubmit={handleReplySubmit}
								handleSetToPrivate={handleSetToPrivate}
								handleSelectTags={handleSelectTags}
								availableTags={availableTags}
								isLoadingMore={isLoadingMoreMilestonesFeed}
							/>
							<div
								ref={setMilestonesSentinelRef}
								style={{ height: '1px', width: '100%' }}
							/>
							{isLoadingMoreMilestonesFeed && (
								<div style={{ textAlign: 'center', padding: '20px' }}>
									<Spin />
								</div>
							)}
							{!hasMoreMilestonesFeedData &&
								milestonesFeedData?.length > 0 &&
								renderNoMoreData()}
						</div>
					),
				}
			: null,
		selectedTags.length > 0
			? {
					key: '5',
					label: (
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<span style={{ marginRight: '4px' }}>Tags:</span>
							<span style={{ fontWeight: 600 }}>{`#${selectedTags[0]}`}</span>
							{selectedTags.length > 1 && (
								<span style={{ marginLeft: '4px' }}>
									{`+ ${selectedTags.length - 1}`}
								</span>
							)}
						</div>
					),
					children: tagsFeedLoading ? (
						<div
							style={{
								minHeight: 800,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Spin />
						</div>
					) : (
						<div className="scroll-card">
							<FeedList
								currentUser={currentUser}
								userAvatarSrc={source}
								feedListData={tagsFeedData}
								handleClapCount={handleClapClick}
								isFeedLoading={tagsFeedLoading}
								handleReplySubmit={handleReplySubmit}
								handleSetToPrivate={handleSetToPrivate}
								handleSelectTags={handleSelectTags}
								availableTags={availableTags}
								isLoadingMore={isLoadingMoreTagsFeed}
							/>
							<div
								ref={setTagsSentinelRef}
								style={{ height: '1px', width: '100%' }}
							/>
							{isLoadingMoreTagsFeed && (
								<div style={{ textAlign: 'center', padding: '20px' }}>
									<Spin />
								</div>
							)}
							{!hasMoreTagsFeedData &&
								tagsFeedData?.length > 0 &&
								renderNoMoreData()}
						</div>
					),
				}
			: null,
	];

	const tagRender = (props) => {
		const { label, closable, onClose } = props;
		const onPreventMouseDown = (event) => {
			event.preventDefault();
			event.stopPropagation();
		};
		return (
			<Tag
				color="blue"
				onMouseDown={onPreventMouseDown}
				closable={closable}
				onClose={onClose}
				style={{
					marginRight: 3,
				}}
			>
				{label}
			</Tag>
		);
	};

	const maxTagRender = (omittedValues) => {
		return (
			<Tag
				color="blue"
				style={{
					marginRight: 0,
				}}
			>
				{`+ ${omittedValues.length} ...`}
			</Tag>
		);
	};

	return (
		<div style={{ height: '100%' }} className="feed-container">
			<div style={{ height: '100% ' }} className="Feed ">
				<div style={{ height: '100%' }} className="d-card feed-wrap">
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<h4 className="feed-title">Feed</h4>
						{availableTags.length > 0 && (
							<div
								style={{
									width: 250,
									maxWidth: '50%',
									display: 'flex',
								}}
							>
								<Select
									mode="multiple"
									allowClear
									className="hashtag-filter-select"
									style={{
										width: '100%',
									}}
									getPopupContainer={() =>
										document.getElementsByClassName('feed-container')[0]
									}
									placeholder="Find by hashtag..."
									onChange={handleSelectTags}
									value={selectedTags}
									options={tagOptions}
									maxTagCount="responsive"
									maxTagPlaceholder={maxTagRender}
									tagRender={tagRender}
									filterOption={filterTagOption}
									notFoundContent={'No Tags Found'}
								/>
							</div>
						)}
					</div>
					<Tabs
						activeKey={activeTab}
						items={tabItems}
						onChange={setActiveTab}
					/>
				</div>
			</div>
		</div>
	);
};

export default Feed;
